import { useState, useCallback, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { googleIntegrationService } from '@/services/google-integration'
import type { GoogleDriveFolderItem } from '@/schemas/google-drive-sync'
import {
  Folder,
  FolderOpen,
  ChevronRight,
  Home,
  Loader2,
  AlertCircle,
} from 'lucide-react'

interface BreadcrumbItem {
  id: string
  name: string
}

interface GoogleDriveFolderPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  onSelect: (folder: { id: string; name: string; path: string }) => void
  /** Future enhancement: navigate to this folder on open */
  initialFolderId?: string
}

export function GoogleDriveFolderPicker({
  open,
  onOpenChange,
  organizationId,
  onSelect,
  initialFolderId: _initialFolderId,
}: GoogleDriveFolderPickerProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [folders, setFolders] = useState<GoogleDriveFolderItem[]>([])
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([
    { id: 'root', name: 'Meu Drive' },
  ])
  const [selectedFolder, setSelectedFolder] = useState<BreadcrumbItem | null>(null)

  const currentFolderId = breadcrumb[breadcrumb.length - 1]?.id || 'root'

  const fetchFolders = useCallback(
    async (parentId: string) => {
      if (!organizationId) return

      try {
        setIsLoading(true)
        setError(null)
        const response = await googleIntegrationService.listDriveFolders(
          organizationId,
          parentId
        )
        setFolders(response.folders)
      } catch (err) {
        console.error('Failed to fetch folders:', err)
        setError('Erro ao carregar pastas')
      } finally {
        setIsLoading(false)
      }
    },
    [organizationId]
  )

  // Load folders when dialog opens or navigation changes
  useEffect(() => {
    if (open) {
      fetchFolders(currentFolderId)
    }
  }, [open, currentFolderId, fetchFolders])

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setBreadcrumb([{ id: 'root', name: 'Meu Drive' }])
      setSelectedFolder(null)
      setError(null)
    }
  }, [open])

  const handleFolderClick = (folder: GoogleDriveFolderItem) => {
    // Select the folder
    setSelectedFolder({ id: folder.id, name: folder.name })
  }

  const handleFolderDoubleClick = (folder: GoogleDriveFolderItem) => {
    // Navigate into the folder
    setBreadcrumb((prev) => [...prev, { id: folder.id, name: folder.name }])
    setSelectedFolder(null)
  }

  const handleBreadcrumbClick = (index: number) => {
    // Navigate to breadcrumb location
    setBreadcrumb((prev) => prev.slice(0, index + 1))
    setSelectedFolder(null)
  }

  const handleConfirm = () => {
    const folder = selectedFolder || breadcrumb[breadcrumb.length - 1]
    const path = breadcrumb
      .slice(1) // Remove "Meu Drive" from path
      .map((item) => item.name)
      .join('/')

    const fullPath = selectedFolder
      ? path
        ? `${path}/${selectedFolder.name}`
        : selectedFolder.name
      : path || 'Meu Drive'

    onSelect({
      id: folder.id,
      name: folder.name,
      path: fullPath,
    })
    onOpenChange(false)
  }

  const getSelectedPath = () => {
    if (selectedFolder) {
      const basePath = breadcrumb
        .slice(1)
        .map((item) => item.name)
        .join('/')
      return basePath ? `${basePath}/${selectedFolder.name}` : selectedFolder.name
    }
    return breadcrumb.map((item) => item.name).join('/')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white">Selecionar Pasta</DialogTitle>
        </DialogHeader>

        {/* Breadcrumb navigation */}
        <div className="flex items-center gap-1 px-2 py-2 bg-gray-800/50 rounded-md overflow-x-auto">
          {breadcrumb.map((item, index) => (
            <div key={item.id} className="flex items-center">
              {index > 0 && <ChevronRight className="h-4 w-4 text-gray-500 mx-1" />}
              <button
                onClick={() => handleBreadcrumbClick(index)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-sm hover:bg-gray-700 transition-colors ${
                  index === breadcrumb.length - 1 && !selectedFolder
                    ? 'text-blue-400 font-medium'
                    : 'text-gray-300'
                }`}
              >
                {index === 0 ? (
                  <Home className="h-4 w-4" />
                ) : (
                  <Folder className="h-4 w-4" />
                )}
                <span className="truncate max-w-[100px]">{item.name}</span>
              </button>
            </div>
          ))}
        </div>

        {/* Folder list */}
        <div className="min-h-[300px] max-h-[400px] overflow-y-auto border border-gray-700 rounded-md">
          {isLoading ? (
            <div className="flex items-center justify-center h-full py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
              <p className="text-red-400 text-sm">{error}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchFolders(currentFolderId)}
                className="mt-2"
              >
                Tentar novamente
              </Button>
            </div>
          ) : folders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <Folder className="h-8 w-8 text-gray-500 mb-2" />
              <p className="text-gray-400 text-sm">Nenhuma pasta encontrada</p>
              <p className="text-gray-500 text-xs mt-1">
                Clique em &quot;Selecionar&quot; para usar esta pasta
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700/50">
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => handleFolderClick(folder)}
                  onDoubleClick={() => handleFolderDoubleClick(folder)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-800/50 transition-colors ${
                    selectedFolder?.id === folder.id
                      ? 'bg-blue-500/10 border-l-2 border-blue-500'
                      : ''
                  }`}
                >
                  {selectedFolder?.id === folder.id ? (
                    <FolderOpen className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  ) : (
                    <Folder className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                  )}
                  <span className="text-sm text-gray-200 truncate">{folder.name}</span>
                  <ChevronRight className="h-4 w-4 text-gray-500 ml-auto flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected folder info */}
        <div className="px-3 py-2 bg-gray-800/30 rounded-md">
          <p className="text-xs text-gray-400">Pasta selecionada:</p>
          <p className="text-sm text-gray-200 truncate mt-0.5">{getSelectedPath()}</p>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-gray-400"
          >
            Cancelar
          </Button>
          <Button onClick={handleConfirm} className="bg-blue-600 hover:bg-blue-700">
            Selecionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
